import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { PwaInstructionsComponent } from '../pwa-instructions.component';

/**
 * app-pwa-instructions-modal
 *
 * The install instructions inside the authenticated app: a modal shell (toolbar,
 * title, close button) around {@link PwaInstructionsComponent}. It holds no copy
 * of the steps — the instructions live in that one child component and this is
 * just the second way to look at them, the first being the public
 * `/install-help` route.
 *
 * Driven the same way as the other configure-and-act modals in the app (e.g.
 * {@link ReprintLabelsModalComponent}): the parent owns the open state via
 * {@link isOpen} and closes on {@link closeModal}, rather than the modal
 * presenting itself through ModalController. That keeps it usable as one line of
 * markup next to whatever help/info button opens it.
 *
 * Whether to offer that button at all is the caller's decision — ask
 * {@link PwaService.canInstall}, so someone already running the installed app is
 * not invited to install it again.
 *
 * Uses the global `.card-popup` shell from src/styles.css (white body, graphite
 * toolbar, 80% height, full width on phones) so it matches the app's existing
 * modals; the only local style is the content padding.
 */
@Component({
  selector: 'app-pwa-instructions-modal',
  standalone: true,
  imports: [CommonModule, IonicModule, PwaInstructionsComponent],
  templateUrl: './pwa-instructions-modal.component.html',
  styleUrls: ['./pwa-instructions-modal.component.css'],
})
export class PwaInstructionsModalComponent {
  /** Whether the modal is presented. Owned by the parent. */
  @Input() isOpen = false;

  /** Modal title. The step content's own heading is suppressed in favour of this. */
  @Input() title = 'Install ReceivingMgr';

  /** Emitted when the user closes the modal (button or backdrop dismiss). */
  @Output() closeModal = new EventEmitter<void>();

  /**
   * Closes the modal. Bound to both the close button and `didDismiss`, so a
   * backdrop dismiss reaches the parent too — otherwise `isOpen` would stay true
   * and the modal could not be reopened.
   */
  public onClose(): void {
    this.closeModal.emit();
  }
}
